// React
import React, { useCallback, useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { PiBookOpenText } from "react-icons/pi";
import { FiVolume2, FiHelpCircle } from "react-icons/fi";

// MUI
import Button from "@mui/material/Button";

// Components
import LearnMoreModal from "../../../../components/LearnMoreModal";
import MultipleChoiceAnswers from "../AnswerMultipleChoice";
import AnswerFillBlank from "../AnswerFillBlank";
import AnswerMatch from "../AnswerMatch";
import AnswerTrueFalse from "../AnswerTrueFalse";

// Styles
import styles from "./index.module.css";
import flashCardStyles from "../../../../components/FlashCardReview/components/Content.module.scss";

// Hooks
import useTailwindDarkMode from "../../../../utils/hooks/darkMode";
import useReadAloudButton from "../../../../hooks/useReadAloudButton";

// Utils
import { createFillBlankAnswer, createMatchingAnswer, findTrueFalseAnswer, type AnswerObject } from "../../../../utils/answerUtils";

// Types
import { QUESTION_TYPES } from "../../../../types";

interface CardProps {
  question: any;
  onAnswerQuestion: (id: string, answer: any, index: number) => void;
  selectedAnswers: number[];
  fromLevel: boolean;
}

const Card: React.FC<CardProps> = ({ question, onAnswerQuestion, selectedAnswers, fromLevel }) => {
  const { t } = useTranslation();
  const isDark = useTailwindDarkMode();

  const { ReadAloudButton, Text } = useReadAloudButton(
    question?.body || '',
    {
      highlightProps: {
        className: styles.highlightedText
      }
    }
  );

  // States
  const [openLearnMoreModal, setOpenLearnMoreModal] = useState(false);
  const [showHint, setShowHint] = useState(false);

  // Handlers
  const handleLearnMore = useCallback(() => {
    setOpenLearnMoreModal(true);
  }, []);

  const handleCloseLearnMoreModal = useCallback(() => {
    setOpenLearnMoreModal(false);
  }, []);

  const handleGetHint = useCallback(() => {
    setShowHint(!showHint);
  }, [showHint]);

  const handleGetHintClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    handleGetHint();
  }, [handleGetHint]);

  const handleAnswerQuestion = useCallback((answer: any, index: number) => {
    onAnswerQuestion(question.id, answer, index);
  }, [onAnswerQuestion, question]);

  const checkMatchingPairsCorrect = useCallback((matches: Record<string, string>) => {
    if (!question.matching_pairs_data) return false;
    
    const correctPairs = question.matching_pairs_data.reduce((acc: Record<string, string>, pair: any) => {
      acc[pair.term] = pair.definition;
      return acc;
    }, {});
    
    return Object.keys(matches).every(term => matches[term] === correctPairs[term]);
  }, [question]);

  const handleCompleteAnswer = useCallback((answer: string) => {
    const answerObj = createFillBlankAnswer(answer, question.correct_answer);
    onAnswerQuestion(question.id, answerObj, 0);
  }, [onAnswerQuestion, question]);

  const handleMatchAnswer = useCallback((matches: Record<string, string>) => {
    const isCorrect = checkMatchingPairsCorrect(matches);
    const answerObj = createMatchingAnswer(matches, isCorrect);
    onAnswerQuestion(question.id, answerObj, 0);
  }, [onAnswerQuestion, question, checkMatchingPairsCorrect]);

  const handleVeracityAnswer = useCallback((answer: boolean) => {
    const questionAnswers = question.answers || [];
    const answerObj = findTrueFalseAnswer(answer, questionAnswers);
    if (answerObj) {
      onAnswerQuestion(question.id, answerObj, 0);
    }
  }, [onAnswerQuestion, question]);

  useEffect(() => {
    if (question?.id) {
      setShowHint(false);
    }
  }, [question?.id]);

  // Render
  if (!question) return null;
  const { type, body, user_answer } = question;

  const renderQuestionInstruction = () => {
    const validTypes = Object.values(QUESTION_TYPES);
    
    if (!validTypes.includes(type)) {
      console.error('Unknown question type:', type);
      return null;
    }
    
    const label = t(`questionCards.${type}.label`);
    const instruction = t(`questionCards.${type}.instruction`);

    return (
      <div className='flex flex-col'>
        <h4 className='text-base font-medium text-stone-700 dark:text-white leading-xllg'>
          {label}
        </h4>
        <p className='text-sm font-medium text-stone-600 dark:text-gray-400 leading-5'>
          {instruction}
        </p>
      </div>
    )
  }

  const renderAnswers = () => {
    const questionAnswers = question.answers || [];
    
    switch (type) {
      case 'multiple_choice':
      case 'single_answer':
        return <MultipleChoiceAnswers answers={questionAnswers} selectedAnswers={selectedAnswers} onSelectAnswer={handleAnswerQuestion} fromLevel={fromLevel}/>
      case 'true_false':
        return <AnswerTrueFalse key={question.id} value={user_answer} answers={questionAnswers} onAnswer={handleVeracityAnswer} />
      case 'fill_in_the_blank':
        return <AnswerFillBlank key={question.id} value={user_answer} correctAnswer={question.correct_answer} onSubmit={handleCompleteAnswer} />
      case 'matching_pairs':
        return <AnswerMatch key={question.id} options={question.matching_pairs_data || []} value={user_answer} onChange={handleMatchAnswer} />
      default:
        return null;
    }
  }

  return (
    <div className={`${styles.container} ${fromLevel? '' : 'bg-gray-100 dark:bg-stone-650'}`}>
      {!fromLevel && (
      <div className='flex flex-row justify-between items-center flex-1 px-11 pt-3 pb-4'>
        {renderQuestionInstruction()}

          <div className={styles.headerActions}>
            <ReadAloudButton className={`dark:bg-zeus-800 dark:text-white`} />
          </div>
        </div>
      )}

      <div className={`${styles.questionContainer} bg-white dark:bg-zeus-800 rounded-lg`}>
        <div className={`${isDark ? styles.questionBodyDark : ''} flex flex-col gap-4 ${fromLevel ? 'px-4' : 'border-b border-stone-550 px-12 pt-12 pb-10' }`}>
          <p className='text-xl font-medium text-stone-700 leading-6 dark:text-white' dir="auto">
            <Text />
          </p>
          {question.type !== QUESTION_TYPES.MATCHING_PAIRS && (
            <>
              {question.hint && !fromLevel && (
                <div className='flex flex-col gap-4'>
                  <button
                    type="button"
                    onClick={handleGetHintClick}
                    className={flashCardStyles.hintButton}
                  >
                    <FiHelpCircle />
                    {showHint ? t('flashcardReview.hideHint') : t('flashcardReview.getHint')}
                  </button>
                </div>
              )}
              {showHint && (
                <div className={`${styles.cardHint} dark:text-white`}>
                  <span className={styles.hintText} dir="auto">{question.hint}</span>
                </div>
              )}
            </>
          )}
        </div>

        <div className={styles.questionAnswers}>
          {renderAnswers()}
        </div>
      </div>

      {/* TODO: Add this back when Chat works. */}
      {/* <LearnMoreModal open={openLearnMoreModal} onClose={handleCloseLearnMoreModal} /> */}
    </div>
  )
}

export default Card;
